import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'motion/react';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../context/CartContext';
import { 
  ArrowRight, 
  ArrowLeft, 
  ShoppingBag, 
  Info, 
  ChevronDown, 
  Play, 
  Maximize2,
  Volume2,
  VolumeX,
  Share2,
  Plus,
  ArrowUpRight
} from 'lucide-react';
import { Link } from 'wouter';
import toast from 'react-hot-toast';

const PAGES = [
  {
    type: 'hero',
    title: 'The Soul of Spices',
    subtitle: 'AjjAs Masala | Edition 2024',
    description: 'Embark on a sensory journey through the ancient spice routes, redefined for the modern kitchen.',
    image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&q=80',
    color: '#BC4B51',
  },
  {
    type: 'story',
    title: 'Heritage & Craft',
    subtitle: 'Our Process',
    description: 'Every grain is handpicked, every blend is a result of years of tradition and experimentation. We don\'t just sell spices; we curate experiences.',
    image: 'https://images.unsplash.com/photo-1509358271058-acd22cc93898?auto=format&fit=crop&q=80',
    color: '#5B8E7D',
  }
];

export default function Brochure() {
  const { products, loading } = useProducts();
  const { addToCart } = useCart();
  const [currentPage, setCurrentPage] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const spiceProducts = products.slice(0, 8); // Grab top 8 for the brochure

  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 0));

  const brochurePages = [
    ...PAGES,
    ...spiceProducts.map(p => ({
      type: 'product',
      ...p
    })),
    {
      type: 'contact',
      title: 'Join Our Community',
      subtitle: 'Subscribe & Connect',
      description: 'The journey doesn\'t end here. Sign up for exclusive recipes and the latest harvests.',
      image: 'https://images.unsplash.com/photo-1596700422627-1832ad30230b?auto=format&fit=crop&q=80',
      color: '#1C1F26',
    }
  ];

  const totalPages = brochurePages.length;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextPage();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') prevPage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages]);

  if (loading) return <div className="h-screen bg-spice-dark flex items-center justify-center text-white font-serif italic">Loading Heritage...</div>;

  return (
    <div className="h-screen w-full bg-[#14161B] overflow-hidden relative font-sans text-white select-none">
      {/* Background Ambience */}
      <div className="absolute inset-0 z-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-br from-spice-red/20 to-transparent mix-blend-overlay" />
        <div className="absolute top-1/4 -left-1/4 w-full h-full bg-spice-accent/10 blur-[120px] rounded-full animate-pulse" />
      </div>

      {/* Persistent UI Elements */}
      <div className="absolute top-0 left-0 w-full p-8 z-50 flex justify-between items-center mix-blend-difference">
        <Link href="/">
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-10 h-10 bg-white text-black flex items-center justify-center rounded-full font-serif font-bold text-xl">A</div>
            <div className="flex flex-col">
              <span className="font-serif font-bold text-lg leading-none tracking-tighter">AJJAS</span>
              <span className="text-[9px] uppercase tracking-[0.3em] font-bold opacity-70">Spice Lookbook</span>
            </div>
          </motion.div>
        </Link>

        <div className="flex items-center gap-6">
          <button onClick={() => setIsMuted(!isMuted)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <div className="h-4 w-px bg-white/20 hidden md:block" />
          <div className="hidden md:flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest opacity-50">
            <span>Scroll or Use Keys</span>
            <ChevronDown className="w-3 h-3 animate-bounce" />
          </div>
        </div>
      </div>

      {/* Pages Container */}
      <div className="h-full w-full relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, scale: 1.1, filter: 'blur(10px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.95, filter: 'blur(10px)' }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="h-full w-full flex items-center justify-center"
          >
            {brochurePages[currentPage].type === 'hero' && (
              <div className="relative w-full h-full flex items-center justify-center p-8 md:p-24 overflow-hidden">
                <motion.div 
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="z-20 text-center max-w-4xl"
                >
                  <span className="text-spice-accent font-bold uppercase tracking-[0.4em] text-xs mb-6 block">{(brochurePages[currentPage] as any).subtitle}</span>
                  <h1 className="text-6xl md:text-9xl font-serif font-bold italic leading-tight mb-8">
                    {(brochurePages[currentPage] as any).title}
                  </h1>
                  <p className="text-lg md:text-xl text-white/60 mb-12 max-w-xl mx-auto leading-relaxed font-light">
                    {brochurePages[currentPage].description}
                  </p>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={nextPage}
                    className="group bg-white text-black px-12 py-5 rounded-full font-bold uppercase tracking-widest text-[11px] flex items-center gap-4 mx-auto hover:bg-spice-accent hover:text-white transition-all duration-500"
                  >
                    Enter the Story <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                  </motion.button>
                </motion.div>
                <div className="absolute inset-0 z-10 bg-black/40" />
                <motion.img 
                  src={(brochurePages[currentPage] as any).image} 
                  className="absolute inset-0 w-full h-full object-cover z-0"
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 10, ease: "linear" }}
                />
              </div>
            )}

            {brochurePages[currentPage].type === 'story' && (
              <div className="w-full h-full grid grid-cols-1 md:grid-cols-2">
                <div className="p-12 md:p-24 flex flex-col justify-center bg-[#1C1F26]">
                  <motion.div
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <span className="text-spice-accent font-bold uppercase tracking-[0.4em] text-xs mb-4 block">Chapter 01</span>
                    <h2 className="text-5xl md:text-7xl font-serif font-bold mb-8 italic">{(brochurePages[currentPage] as any).title}</h2>
                    <p className="text-xl text-white/50 leading-relaxed max-w-lg mb-12 font-light">
                      {brochurePages[currentPage].description}
                    </p>
                    <div className="flex items-center gap-10">
                      <div>
                        <span className="text-4xl font-serif italic text-white mb-2 block">100%</span>
                        <span className="text-[10px] uppercase tracking-widest font-bold opacity-40">Organic Grade</span>
                      </div>
                      <div className="h-10 w-px bg-white/10" />
                      <div>
                        <span className="text-4xl font-serif italic text-white mb-2 block">45+</span>
                        <span className="text-[10px] uppercase tracking-widest font-bold opacity-40">Select Regions</span>
                      </div>
                    </div>
                  </motion.div>
                </div>
                <div className="relative overflow-hidden group">
                  <motion.img 
                    src={(brochurePages[currentPage] as any).image} 
                    className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110"
                    transition={{ duration: 1.5 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#1C1F26]" />
                </div>
              </div>
            )}

            {brochurePages[currentPage].type === 'product' && (
              <div className="w-full h-full flex flex-col md:flex-row items-center">
                 <div className="w-full md:w-1/2 h-1/2 md:h-full relative overflow-hidden group">
                  <motion.img 
                    src={(brochurePages[currentPage] as any).image} 
                    className="w-full h-full object-cover transition-transform duration-[3s] group-hover:scale-125"
                    transition={{ duration: 1.5 }}
                  />
                  <div className="absolute inset-0 bg-black/20" />
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute top-12 left-12"
                  >
                    <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center backdrop-blur-md">
                      <span className="text-4xl font-serif italic text-white">{(currentPage - 1).toString().padStart(2, '0')}</span>
                    </div>
                  </motion.div>
                </div>

                <div className="w-full md:w-1/2 p-12 md:p-24 bg-[#14161B] relative">
                  <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <span className="px-3 py-1 bg-spice-accent/10 border border-spice-accent/20 text-spice-accent text-[9px] font-bold uppercase tracking-widest rounded-full">{(brochurePages[currentPage] as any).category}</span>
                      <span className="text-[9px] text-white/30 uppercase font-bold tracking-widest">Premium Collection</span>
                    </div>
                    
                    <h2 className="text-5xl md:text-8xl font-serif font-bold text-white mb-6 leading-none">{(brochurePages[currentPage] as any).name}</h2>
                    <p className="text-lg text-white/50 leading-relaxed mb-10 max-w-sm font-light">
                      {(brochurePages[currentPage] as any).description?.substring(0, 160)}...
                    </p>

                    <div className="flex flex-col gap-6">
                      <div className="flex items-end gap-3 mb-4">
                        <span className="text-3xl font-bold">₹{(brochurePages[currentPage] as any).price}</span>
                        <span className="text-sm text-white/20 uppercase tracking-widest font-bold pb-1">Starting Price</span>
                      </div>

                      <div className="flex gap-4">
                        <motion.button 
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            const p = brochurePages[currentPage] as any;
                            addToCart(p as any, p.variants[0]?.weight || '100g', 1);
                            toast.success(`Added ${p.name} to cart`);
                          }}
                          className="flex-1 bg-white text-black h-16 rounded-none font-bold uppercase tracking-[0.2em] text-[10px] hover:bg-spice-accent hover:text-white transition-all flex items-center justify-center gap-3 px-8"
                        >
                          <ShoppingBag className="w-4 h-4" /> Collect Item
                        </motion.button>
                        <Link href={`/product/${(brochurePages[currentPage] as any).id}`}>
                          <motion.button 
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-16 h-16 border border-white/10 text-white rounded-none flex items-center justify-center hover:bg-white/5 transition-all"
                          >
                            <ArrowUpRight className="w-5 h-5" />
                          </motion.button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            )}

            {brochurePages[currentPage].type === 'contact' && (
              <div className="w-full h-full flex flex-col items-center justify-center p-12 relative overflow-hidden">
                <motion.div 
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="z-20 text-center max-w-2xl bg-white/5 backdrop-blur-3xl p-16 border border-white/10 rounded-[4rem]"
                >
                  <h2 className="text-6xl md:text-8xl font-serif font-bold italic mb-8">{(brochurePages[currentPage] as any).title}</h2>
                  <p className="text-lg text-white/50 mb-12 font-light leading-relaxed">
                    {brochurePages[currentPage].description}
                  </p>
                  
                  <div className="flex flex-col md:flex-row gap-4 justify-center">
                    <input 
                      type="email" 
                      placeholder="YOUR@EMAIL.COM" 
                      className="bg-white/5 border border-white/10 px-8 py-5 rounded-full font-bold text-xs outline-none focus:border-spice-accent transition-colors min-w-[300px]"
                    />
                    <button className="bg-spice-accent text-white px-10 py-5 rounded-full font-bold uppercase tracking-widest text-[11px] hover:bg-white hover:text-black transition-all">
                      Subscribe
                    </button>
                  </div>

                  <div className="mt-16 flex justify-center gap-12 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
                    <span className="text-[10px] font-bold uppercase tracking-[0.5em] cursor-pointer hover:text-spice-accent">Instagram</span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.5em] cursor-pointer hover:text-spice-accent">Behance</span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.5em] cursor-pointer hover:text-spice-accent">Pinterest</span>
                  </div>
                </motion.div>
                
                <div className="absolute inset-0 z-10 bg-black/60" />
                <img src={(brochurePages[currentPage] as any).image} className="absolute inset-0 w-full h-full object-cover z-0" />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-50 flex items-center gap-6">
          <button 
            onClick={prevPage}
            disabled={currentPage === 0}
            className={`p-3 rounded-full border border-white/10 transition-all ${currentPage === 0 ? 'opacity-20 translate-x-4 pointer-events-none' : 'hover:bg-white hover:text-black hover:-translate-x-1'}`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3">
             {brochurePages.map((_, idx) => (
                <div 
                  key={idx}
                  onClick={() => setCurrentPage(idx)}
                  className={`h-1.5 rounded-full transition-all duration-500 cursor-pointer ${idx === currentPage ? 'w-12 bg-spice-accent px-4' : 'w-1.5 bg-white/20 hover:bg-white/40'}`}
                />
             ))}
          </div>

          <button 
            onClick={nextPage}
            disabled={currentPage === totalPages - 1}
            className={`p-3 rounded-full border border-white/10 transition-all ${currentPage === totalPages - 1 ? 'opacity-20 -translate-x-4 pointer-events-none' : 'hover:bg-white hover:text-black hover:translate-x-1'}`}
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Page Count */}
        <div className="absolute bottom-12 right-12 z-50 mix-blend-difference hidden md:block">
          <div className="flex items-end gap-2">
            <span className="text-4xl font-serif italic font-bold">{(currentPage + 1).toString().padStart(2, '0')}</span>
            <span className="text-xs font-bold opacity-30 pb-2">/ {totalPages.toString().padStart(2, '0')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
